import { Link } from '@inertiajs/react'
import AppLayout from '../../components/AppLayout'

const STATE_META = {
  running: { label: '実行中', bg: '#fef3c7', text: '#92400e' },
  done:    { label: '完了',   bg: '#dcfce7', text: '#166534' },
  error:   { label: 'エラー', bg: '#fee2e2', text: '#991b1b' },
}

const FIELD_LABELS = {
  weight_delivery: '重み(お届)',
  weight_appeal:   '重み(魅力)',
  threshold:       '閾値',
  upper_limit:     '上限',
}

function fmt(v, isPct) {
  if (v == null) return '—'
  if (isPct) return `${(parseFloat(v) * 100).toFixed(1)}%`
  return parseFloat(v).toFixed(3)
}

function formatDuration(sec) {
  if (sec == null) return '—'
  if (sec < 60) return `${sec}秒`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}分${s}秒`
}

function DiffCell({ before, after, isPct, changed }) {
  if (!changed) {
    return <span style={{ color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>{fmt(after, isPct)}</span>
  }
  return (
    <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
      <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>{fmt(before, isPct)}</span>
      <span style={{ color: '#166534', fontWeight: 600 }}>{' → '}{fmt(after, isPct)}</span>
    </span>
  )
}

export default function RecomputeRunsShow({ run, diffs }) {
  const sm = STATE_META[run.state] || { label: run.state, bg: '#e4e4e4', text: '#555' }
  const changedCount = diffs.filter(d => d.any_changed).length

  return (
    <AppLayout title={`再評価実行 #${run.id}`}>
      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Link href="/recompute_runs" style={{ color: '#4338ca', textDecoration: 'none', fontSize: '13px' }}>
          ← 実行履歴一覧
        </Link>
      </div>

      <div style={{ background: 'white', borderRadius: '10px', padding: '20px 24px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>再評価実行 #{run.id}</span>
          <span style={{ padding: '3px 10px', borderRadius: '4px', background: sm.bg, color: sm.text, fontWeight: 600, fontSize: '12px' }}>{sm.label}</span>
        </div>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto 1fr', gap: '6px 16px', fontSize: '12px', margin: 0 }}>
          <dt style={dtStyle}>開始</dt><dd style={ddStyle}>{new Date(run.started_at).toLocaleString('ja-JP')}</dd>
          <dt style={dtStyle}>完了</dt><dd style={ddStyle}>{run.finished_at ? new Date(run.finished_at).toLocaleString('ja-JP') : '—'}</dd>
          <dt style={dtStyle}>所要時間</dt><dd style={ddStyle}>{formatDuration(run.duration_sec)}</dd>
          <dt style={dtStyle}>実行者</dt><dd style={ddStyle}>{run.triggered_by || '—'}</dd>
          <dt style={dtStyle}>件数</dt><dd style={ddStyle}>{run.done_count} / {run.total_count}</dd>
          <dt style={dtStyle}>エラー</dt><dd style={{ ...ddStyle, color: run.error_count > 0 ? '#991b1b' : '#334155' }}>{run.error_count}</dd>
        </dl>
        {run.error_message && (
          <div style={{ marginTop: '12px', padding: '8px 12px', background: '#fef2f2', borderRadius: '6px', color: '#991b1b', fontSize: '12px', whiteSpace: 'pre-wrap' }}>
            {run.error_message}
          </div>
        )}
      </div>

      <h2 style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
        指標重み — 実行時点 vs 現在
        {changedCount > 0 && (
          <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '4px', background: '#fef3c7', color: '#92400e', fontSize: '11px' }}>
            {changedCount} 件変更あり
          </span>
        )}
      </h2>

      <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={th}>カテゴリ</th>
              <th style={th}>指標</th>
              <th style={th}>{FIELD_LABELS.weight_delivery}</th>
              <th style={th}>{FIELD_LABELS.weight_appeal}</th>
              <th style={th}>{FIELD_LABELS.threshold}</th>
              <th style={th}>{FIELD_LABELS.upper_limit}</th>
            </tr>
          </thead>
          <tbody>
            {diffs.map((d, i) => (
              <tr key={d.key} style={{ borderBottom: '1px solid #f1f5f9', background: d.any_changed ? '#fffbeb' : (i % 2 === 0 ? 'white' : '#fafafa') }}>
                <td style={{ ...td, color: '#64748b', fontSize: '11px' }}>{d.category}</td>
                <td style={td}>
                  <div style={{ fontWeight: 500 }}>{d.name}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#94a3b8' }}>{d.key}</div>
                  {d.only_in && (
                    <span style={{ fontSize: '10px', color: '#991b1b' }}>
                      {d.only_in === 'snapshot' ? '※ 現在は削除済み' : '※ 実行時は未存在'}
                    </span>
                  )}
                </td>
                <td style={td}><DiffCell before={d.changes.weight_delivery.before} after={d.changes.weight_delivery.after} isPct changed={d.changes.weight_delivery.changed} /></td>
                <td style={td}><DiffCell before={d.changes.weight_appeal.before}   after={d.changes.weight_appeal.after}   isPct changed={d.changes.weight_appeal.changed} /></td>
                <td style={td}><DiffCell before={d.changes.threshold.before}       after={d.changes.threshold.after}             changed={d.changes.threshold.changed} /></td>
                <td style={td}><DiffCell before={d.changes.upper_limit.before}     after={d.changes.upper_limit.after}           changed={d.changes.upper_limit.changed} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  )
}

const dtStyle = { color: '#94a3b8', fontWeight: 500, whiteSpace: 'nowrap' }
const ddStyle = { color: '#334155', margin: 0 }
const th = { padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }
const td = { padding: '9px 12px', verticalAlign: 'middle' }
